import type {ChangeEvent, KeyboardEvent, MouseEvent, ReactElement} from 'react';
import {useEffect, useRef, useState} from 'react';
import {ClickableDiv} from '@Components/clickable-div';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import type {FolderCardProps} from './folder-card.types';
import {FOLDER_CARD_ILLUSTRATION_PATH, FOLDER_NAME_INPUT_KEYS, KEYS_THAT_KEEP_SEEDED_FOLDER_NAME} from './folder-card.types';
import styles from './folder-card.module.scss';

export function FolderCard({
  name,
  onClick,
  isSelected = false,
  isDropTarget = false,
  isShared = false,
  isEditing = false,
  onNameCommit,
  onEditCancel,
  moreOptionsSlot,
  shouldAlwaysShowMoreOptions = false,
}: FolderCardProps): ReactElement {
  const nameInputRef = useRef<HTMLInputElement>(null);
  const isNameEditResolvedRef = useRef(false);
  const isSeedReplacementPendingRef = useRef(false);
  const wasEditingWhenPressedRef = useRef(false);
  const [editedName, setEditedName] = useState(name);

  useEffect(() => {
    if (isEditing && nameInputRef.current) {
      isNameEditResolvedRef.current = false;
      isSeedReplacementPendingRef.current = true;
      setEditedName(name);
      nameInputRef.current.focus();
      nameInputRef.current.setSelectionRange(name.length, name.length);
    }
  }, [isEditing]);

  const commitNameEdit = (): void => {
    if (isNameEditResolvedRef.current) {
      return;
    }
    isNameEditResolvedRef.current = true;
    onNameCommit?.(editedName.trim());
  };

  const cancelNameEdit = (): void => {
    if (isNameEditResolvedRef.current) {
      return;
    }
    isNameEditResolvedRef.current = true;
    onEditCancel?.();
  };

  const onNameInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setEditedName(getNameForInputValue(event.target.value));
    isSeedReplacementPendingRef.current = false;
  };

  /**
   * While the seeded name is untouched the caret sits at its end, so the first change can only be
   * an insertion there — Finder-style, the insertion replaces the whole seed. Any other change
   * (the user moved the caret first) is a normal edit.
   */
  const getNameForInputValue = (inputValue: string): string => {
    if (isSeedReplacementPendingRef.current && inputValue.startsWith(name) && inputValue.length > name.length) {
      return inputValue.slice(name.length);
    }
    return inputValue;
  };

  const onNameInputKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (KEYS_THAT_KEEP_SEEDED_FOLDER_NAME.includes(event.key)) {
      isSeedReplacementPendingRef.current = false;
    }
    // Both keys belong to the edit: an Escape that kept bubbling would also close the modal or popup holding the card.
    if (event.key === FOLDER_NAME_INPUT_KEYS.COMMIT) {
      event.stopPropagation();
      commitNameEdit();
    } else if (event.key === FOLDER_NAME_INPUT_KEYS.CANCEL) {
      event.stopPropagation();
      cancelNameEdit();
    }
  };

  const onNameInputMouseDown = (): void => {
    isSeedReplacementPendingRef.current = false;
  };

  const onCardPointerDownCapture = (): void => {
    wasEditingWhenPressedRef.current = isEditing;
  };

  /**
   * A press that starts mid-edit blurs the name input, which commits the name and ends the edit before its click
   * lands. That click only finished the edit, so it must not also open or pick the card.
   */
  const onCardClickCapture = (event: MouseEvent<HTMLDivElement>): void => {
    if (wasEditingWhenPressedRef.current) {
      event.stopPropagation();
    }
  };

  const getFolderVisual = (): ReactElement => {
    if (isShared) {
      return (
        <div className={`flex-center ${styles.sharedFolderIconContainer}`}>
          <Icon icon="icon-collection-shared" size={IconSize.SIZE_ICON_24} />
        </div>
      );
    }
    return <img alt="" aria-hidden className={styles.folderIllustration} src={window.PMW.util.asset_url(FOLDER_CARD_ILLUSTRATION_PATH)} />;
  };

  const getNameContent = (): ReactElement => {
    if (isEditing) {
      return (
        <span className={styles.folderNameInputSizer} data-value={editedName}>
          <input
            aria-label={window.i18next.t('pmwjs_rename')}
            className={styles.folderNameInput}
            onBlur={commitNameEdit}
            onChange={onNameInputChange}
            onKeyDown={onNameInputKeyDown}
            onMouseDown={onNameInputMouseDown}
            ref={nameInputRef}
            size={1}
            type="text"
            value={editedName}
          />
        </span>
      );
    }
    return <Text val={name} size={TextSize.MD} bold className={`-truncated-text ${styles.folderName}`} />;
  };

  const getMoreOptions = (): ReactElement | null => {
    if (!moreOptionsSlot || isEditing) {
      return null;
    }
    return <div className={`${styles.moreOptionsContainer} ${shouldAlwaysShowMoreOptions ? styles.alwaysShowMoreOptions : ''}`}>{moreOptionsSlot}</div>;
  };

  const getCardContent = (): ReactElement => {
    return (
      <>
        {getFolderVisual()}
        <div className={styles.folderNameContainer}>{getNameContent()}</div>
        {getMoreOptions()}
      </>
    );
  };

  // A drag hovering the card reads exactly like a picked card, so the drop state reuses the selected styles.
  const cardClassName = `${styles.folderCard} ${isSelected || isDropTarget ? styles.selectedFolderCard : ''}`;

  if (!onClick || isEditing) {
    return (
      <div className={cardClassName} onPointerDownCapture={onCardPointerDownCapture} onClickCapture={onCardClickCapture}>
        {getCardContent()}
      </div>
    );
  }
  return (
    <ClickableDiv
      className={`${cardClassName} ${styles.clickableFolderCard}`}
      onClick={onClick}
      onPointerDownCapture={onCardPointerDownCapture}
      onClickCapture={onCardClickCapture}
      aria-pressed={isSelected}
    >
      {getCardContent()}
    </ClickableDiv>
  );
}
